import React from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import TaskTemplateFavoriteHeart from '@/components/Tasks/TaskTemplateFavoriteHeart';

function TemplatePinCard({ template, onPress, onSchedulePress, width = '100%', stretchHeight = false, layout = 'grid', preview, discovery = false }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();
  const palette = getCatalogPalette(theme);
  const wideList = layout === 'list' && !isXS && !isSM;

  return (
    <View style={{ width, borderWidth: 1, borderColor: palette.border, borderRadius: 16, backgroundColor: palette.surface,
      padding: discovery ? 20 : 16, gap: discovery ? 14 : 16, flexDirection: wideList ? 'row' : 'column', alignItems: wideList ? 'center' : 'stretch',
      ...(stretchHeight ? { flexGrow: 1, ...(Platform.OS === 'web' ? { height: '100%' } : {}) } : {}) }}>
      <Pressable onPress={() => onPress?.(template)} accessibilityRole={discovery ? "button" : "link"} accessibilityLabel={template?.name}
        style={({ hovered, pressed }) => ({ flex: 1, minWidth: 0, flexDirection: 'row', gap: 14, alignItems: 'flex-start', flexWrap: preview || discovery ? 'wrap' : 'nowrap',
          opacity: pressed ? 0.7 : 1, borderRadius: 10, backgroundColor: hovered ? palette.hover : 'transparent' })}>
        {!!preview && <View style={{ width: '100%', borderRadius: 10, overflow: 'hidden' }}>{preview}</View>}
        {!preview && !discovery && <View style={{ width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: palette.preview }}>
          <Icon name={template?.icon || 'sparkles'} size={20} color={palette.icon} />
        </View>}
        <View style={{ flex: 1, minWidth: 0, gap: discovery ? 10 : 5 }}>
          {discovery && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}><Icon name={template?.icon || 'sparkles'} size={14} color={palette.icon} /><Text size="xs" color="secondary">Scheduled task</Text></View>}
          <Text weight="semibold" size="n" numberOfLines={discovery ? 3 : 2} style={discovery ? { lineHeight: 24 } : undefined}>{template?.name}</Text>
          {!!template?.description && <Text size="s" color="secondary" numberOfLines={discovery ? 3 : 2} style={{ lineHeight: 20 }}>{template.description}</Text>}
        </View>
      </Pressable>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        {!discovery && <TaskTemplateFavoriteHeart template={template} />}
        <Pressable accessibilityRole="button" accessibilityLabel={t('pages.tasks.scheduleIt')}
          onPress={() => (onSchedulePress ?? onPress)?.(template)}
          style={({ hovered, pressed }) => ({ minHeight: 36, paddingHorizontal: 14, borderRadius: 9, flexDirection: 'row', alignItems: 'center', gap: 8,
            marginLeft: discovery ? 0 : 'auto', backgroundColor: hovered ? palette.hover : palette.preview, opacity: pressed ? 0.7 : 1 })}>
          <Text size="xs" weight="semibold">{t('pages.tasks.scheduleShort')}</Text>
          <Icon name="plus" size={11} color={palette.icon} />
        </Pressable>
      </View>
    </View>
  );
}

export function TemplatePinCardPlaceholder({ width = '100%', layout = 'grid' }) {
  const { theme } = useTheme();
  const palette = getCatalogPalette(theme);
  return <View style={{ width, padding: 18, gap: 12, minHeight: layout === 'list' ? 92 : 156, borderRadius: 16, borderWidth: 1, borderColor: palette.border }}>
    <View style={{ width: '55%', height: 16, borderRadius: 4, backgroundColor: palette.preview }} />
    <View style={{ width: '90%', height: 12, borderRadius: 4, backgroundColor: palette.preview }} />
  </View>;
}

export default React.memo(TemplatePinCard);
